import { useState, type FormEvent } from "react";
import { api, unwrap } from "../api/client";
import type { InputMode, InputProtocol, InputView } from "../api/types";

type Deinterlace = InputView["deinterlace"];
import { useClientLimits } from "../lib/auth";
import { randomKey } from "../lib/format";
import { N_, t, tc } from "../lib/i18n";
import { OwnerSelect } from "./OwnerSelect";
import { Button, Checkbox, Field, FormError, Modal, Segmented, Select, TextInput } from "./ui";

const PUSH_PROTOCOLS: { value: InputProtocol; label: string }[] = [
  { value: "srt", label: N_("SRT (recommandé)") }, { value: "rtmp", label: "RTMP (OBS, vMix…)" },
  { value: "rtsp", label: "RTSP" }, { value: "whip", label: "WebRTC (WHIP)" },
];
const PULL_PROTOCOLS: { value: InputProtocol; label: string }[] = [
  { value: "rtsp", label: N_("RTSP (caméra IP)") }, { value: "srt", label: N_("SRT (encodeur en listener)") },
  { value: "udp", label: N_("UDP MPEG-TS (unicast ou multicast)") }, { value: "hls", label: "HLS" },
  { value: "rtmp", label: "RTMP" },
];
const CLIENT_PULL = new Set<InputProtocol>(["srt", "rtmp", "rtsp", "hls"]);   // comptes clients : pas d'UDP
const SOURCE_PLACEHOLDER: Partial<Record<InputProtocol, string>> = {
  rtsp: N_("rtsp://admin:motdepasse@192.168.1.10:554/stream1"),
  srt: "srt://10.0.0.5:9000?streamid=live",
  udp: N_("udp://0.0.0.0:9001  ou  udp://239.1.1.1:5000"),
  hls: N_("https://exemple.com/live/index.m3u8"),
  rtmp: N_("rtmp://serveur/app/flux"),
};

interface Draft {
  id: string; name: string; mode: InputMode; protocol: InputProtocol;
  source_url: string; stream_key: string; srt_passphrase: string; enabled: boolean; record: boolean; content_check: boolean;
  deinterlace: Deinterlace; owner: string | null;
}

function draftFrom(input?: InputView): Draft {
  return {
    id: input?.id ?? "", name: input?.name ?? "", mode: input?.mode ?? "push", protocol: input?.protocol ?? "srt",
    source_url: input?.source_url ?? "", stream_key: input?.stream_key ?? (input ? "" : randomKey(16)),
    srt_passphrase: input?.srt_passphrase ?? "", enabled: input?.enabled ?? true, record: input?.record ?? false,
    content_check: input?.content_check ?? true, deinterlace: input?.deinterlace ?? "off", owner: input?.owner ?? null,
  };
}

export function InputForm({ input, onClose, onSaved }: {
  input?: InputView; onClose: () => void; onSaved: (saved: InputView) => void;
}) {
  const editing = Boolean(input);
  const [d, setD] = useState<Draft>(() => draftFrom(input));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((prev) => ({ ...prev, [k]: v }));
  const limits = useClientLimits();          // compte client : options limitées (le serveur vérifie aussi)
  const pull = limits ? PULL_PROTOCOLS.filter((p) => CLIENT_PULL.has(p.value)) : PULL_PROTOCOLS;

  const protocols = d.mode === "push" ? PUSH_PROTOCOLS : pull;
  const setMode = (mode: InputMode) => {
    const list = mode === "push" ? PUSH_PROTOCOLS : pull;
    setD((prev) => ({ ...prev, mode, protocol: list.some((p) => p.value === prev.protocol) ? prev.protocol : list[0]!.value }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name: d.name.trim(), mode: d.mode, protocol: d.protocol, enabled: d.enabled, record: d.record, content_check: d.content_check,
      deinterlace: d.deinterlace, owner: d.owner,
      source_url: d.mode === "pull" ? d.source_url.trim() : null,
      stream_key: d.mode === "push" ? d.stream_key.trim() || null : null,
      srt_passphrase: d.mode === "push" && d.protocol === "srt" ? d.srt_passphrase.trim() || null : null,
    };
    try {
      const saved = editing
        ? await unwrap(api.PUT("/api/inputs/{input_id}", { params: { path: { input_id: input!.id } }, body }))
        : await unwrap(api.POST("/api/inputs", { body: { ...body, id: d.id.trim() } }));
      onSaved(saved);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={editing ? t("Modifier « {name} »", { name: input!.name }) : t("Nouvelle entrée")} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Annuler")}</Button>
        <Button variant="primary" type="submit" form="input-form" disabled={busy}>
          {editing ? t("Enregistrer") : t("Créer l'entrée")}
        </Button>
      </>}>
      <form id="input-form" onSubmit={submit} className="flex flex-col gap-4">
        {!editing && (
          <Field label={tc("entrée", "Identifiant")} htmlFor="in-id" hint={t("Minuscules, chiffres, - et _. Il apparaît dans les URL (ex. cam1) et ne change plus ensuite.")}>
            <TextInput id="in-id" required value={d.id} onChange={(e) => set("id", e.target.value.toLowerCase())}
              pattern="[a-z0-9][a-z0-9_\-]{0,62}" placeholder="cam1" autoFocus />
          </Field>
        )}
        <OwnerSelect id="in-owner" value={d.owner} onChange={(v) => set("owner", v)} />
        <Field label={t("Nom affiché")} htmlFor="in-name">
          <TextInput id="in-name" required maxLength={80} value={d.name} onChange={(e) => set("name", e.target.value)}
            placeholder={t("Caméra plateau")} autoFocus={editing} />
        </Field>
        <Field label={t("Mode")} hint={d.mode === "push"
          ? t("L'encodeur se connecte à la gateway. Vous lui donnerez l'URL affichée après création.")
          : t("La gateway va chercher le flux à l'adresse indiquée.")}>
          <Segmented label={t("Mode")} value={d.mode} onChange={setMode}
            options={[{ value: "push", label: t("Push (l'encodeur envoie)") }, { value: "pull", label: t("Pull (la gateway récupère)") }]} />
        </Field>
        <Field label={t("Protocole")} htmlFor="in-proto">
          <Select id="in-proto" value={d.protocol} onChange={(e) => set("protocol", e.target.value as InputProtocol)}>
            {protocols.map((p) => <option key={p.value} value={p.value}>{t(p.label)}</option>)}
          </Select>
        </Field>
        {d.mode === "pull" ? (
          <Field label={t("URL de la source")} htmlFor="in-src" hint={d.protocol === "udp" ? t("Un port par source UDP : deux émetteurs sur le même port mélangent leurs flux.") : undefined}>
            <TextInput id="in-src" required value={d.source_url} onChange={(e) => set("source_url", e.target.value)}
              placeholder={t(SOURCE_PLACEHOLDER[d.protocol] ?? "")} className="font-mono text-[13px]" />
          </Field>
        ) : (
          <>
            <Field label={t("Clé de flux")} htmlFor="in-key" hint={limits || d.owner
              ? t("Vide : une clé est tirée au hasard (obligatoire pour un compte client).")
              : t("Vide = n'importe quel encodeur peut publier sur cette entrée.")}>
              <div className="flex gap-2">
                <TextInput id="in-key" maxLength={128} value={d.stream_key} onChange={(e) => set("stream_key", e.target.value)}
                  className="font-mono text-[13px]" />
                <Button onClick={() => set("stream_key", randomKey(16))}>{t("Générer")}</Button>
              </div>
            </Field>
            {d.protocol === "srt" && (
              <Field label={t("Passphrase SRT (chiffrement AES)")} htmlFor="in-pass" hint={t("10 à 79 caractères. Vide = flux non chiffré.")}>
                <div className="flex gap-2">
                  <TextInput id="in-pass" value={d.srt_passphrase} onChange={(e) => set("srt_passphrase", e.target.value)}
                    className="font-mono text-[13px]" />
                  <Button onClick={() => set("srt_passphrase", randomKey(24))}>{t("Générer")}</Button>
                </div>
              </Field>
            )}
          </>
        )}
        <Checkbox id="in-enabled" label={t("Entrée active")} checked={d.enabled} onChange={(v) => set("enabled", v)} />
        {!limits && <div className="flex flex-col gap-1">
          <Checkbox id="in-record" label={t("Enregistrement de secours")} checked={d.record} onChange={(v) => set("record", v)} />
          <p className="pl-6 text-[12px] text-faint">
            {t("Le flux est enregistré tel quel (fichiers MP4, lisibles par VLC), puis effacé automatiquement après la durée de conservation du serveur. Prévoir l'espace disque : environ 1 Go par heure à 2,5 Mb/s.")}
          </p>
        </div>}
        <div className="flex flex-col gap-1">
          <Checkbox id="in-content" label={t("Surveiller le contenu (image noire, figée, silence)")} checked={d.content_check}
            onChange={(v) => set("content_check", v)} />
          <p className="pl-6 text-[12px] text-faint">
            {t("Alarme si l'image reste noire ou figée, ou si le son est coupé ; affiche aussi le niveau sonore. À décocher pour une source volontairement fixe (mire, image d'attente).")}
          </p>
        </div>
        {(!limits || limits.transcode) && <Field label={t("Source entrelacée")} htmlFor="in-deint"
          hint={t("Pour une source entrelacée (1080i) : la gateway la convertit en progressif pour la preview, les vignettes et toutes les sorties. Réencodage permanent : environ 1 cœur en 25p, 3 cœurs en 50p (1080).")}>
          <Select id="in-deint" value={d.deinterlace} onChange={(e) => set("deinterlace", e.target.value as Deinterlace)}>
            <option value="off">{t("Laisser tel quel")}</option>
            <option value="frame">{t("Convertir en progressif, même cadence (1080i50 → 1080p25)")}</option>
            <option value="field">{t("Convertir en progressif, double cadence (1080i50 → 1080p50, plus fluide)")}</option>
          </Select>
        </Field>}
        <FormError message={error} />
      </form>
    </Modal>
  );
}
